import React, { useEffect, useRef, useState } from 'react';

import {
  applyConsent,
  getConsent,
  hasGpcSignal,
  onOpenCookiePreferencesRequested,
} from '~/shared/consent/consent-manager';

export const CookiePreferencesPanel = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [ads, setAds] = useState(true);
  const [optout, setOptout] = useState(false);
  const [gpcApplied, setGpcApplied] = useState(false);
  const dialogRef = useRef(null);

  useEffect(() => {
    return onOpenCookiePreferencesRequested(() => {
      const current = getConsent();
      setAnalytics(current.analytics);
      setAds(current.ads);
      setOptout(current.optout);
      setGpcApplied(hasGpcSignal());
      setIsOpen(true);
    });
  }, []);

  // Keyboard and screen-reader users: move focus into the dialog when it opens,
  // keep Tab inside it, close on Escape, and hand focus back on close.
  useEffect(() => {
    if (!isOpen) return undefined;

    const opener = document.activeElement;
    const dialog = dialogRef.current;
    const getFocusable = () =>
      Array.from(dialog?.querySelectorAll('button, input:not([disabled]), a[href]') || []);

    getFocusable()[0]?.focus();

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setIsOpen(false);
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = getFocusable();
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (opener instanceof HTMLElement && document.contains(opener)) opener.focus();
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const close = () => setIsOpen(false);

  const toggleOptout = (checked) => {
    setOptout(checked);
    if (checked) {
      setAds(false);
    }
  };

  const save = ({ analytics: nextAnalytics, ads: nextAds, optout: nextOptout, reload }) => {
    const prev = getConsent();
    applyConsent({ analytics: nextAnalytics, ads: nextAds, optout: nextOptout });
    close();
    const turnedSomethingOff = (prev.analytics && !nextAnalytics) || (prev.ads && !nextAds);
    if (reload || turnedSomethingOff) {
      window.location.reload();
    }
  };

  return (
    <div className="cookie-panel-overlay" role="presentation" onClick={close}>
      <div
        ref={dialogRef}
        className="cookie-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Cookie and Tracking Preferences"
        onClick={(event) => event.stopPropagation()}
      >
        <button type="button" className="cookie-panel__close" aria-label="Close" onClick={close}>
          &times;
        </button>
        <h4 className="cookie-panel__title">Cookie &amp; Tracking Preferences</h4>
        <p className="cookie-panel__lead">
          Strictly necessary cookies are required for the site to function and cannot be turned off. You can turn the
          other categories on or off and change your choices at any time by returning to this panel.
        </p>

        <div className="cookie-panel__cat">
          <div>
            <b>Strictly Necessary</b>
            <p>Required to operate the site, route your call, and maintain security.</p>
          </div>
          <span className="cookie-panel__always">Always active</span>
        </div>

        <div className="cookie-panel__cat">
          <div>
            <b>Analytics &amp; Performance</b>
            <p>Helps us understand how visitors use the site so we can improve it.</p>
          </div>
          <label className="cookie-panel__switch">
            <input type="checkbox" aria-label="Analytics and Performance cookies" checked={analytics} onChange={(event) => setAnalytics(event.target.checked)} />
            <span className="cookie-panel__track" />
          </label>
        </div>

        <div className="cookie-panel__cat">
          <div>
            <b>Advertising &amp; Targeting</b>
            <p>Used to measure and improve our marketing.</p>
          </div>
          <label className="cookie-panel__switch">
            <input type="checkbox" aria-label="Advertising and Targeting cookies" checked={ads} disabled={optout} onChange={(event) => setAds(event.target.checked)} />
            <span className="cookie-panel__track" />
          </label>
        </div>

        <div className="cookie-panel__optout">
          <label>
            <input type="checkbox" checked={optout} onChange={(event) => toggleOptout(event.target.checked)} />
            Opt out of the sale or sharing of my personal information
          </label>
          <p>
            Depending on the technologies in use, some of our sharing of personal information may be treated as a
            &ldquo;sale&rdquo; or &ldquo;sharing&rdquo; under California law. Selecting this option opts you out. We
            also honor opt-out preference signals, including Global Privacy Control (GPC). For more detail, see our
            Privacy Policy.
          </p>
          {gpcApplied && (
            <p className="cookie-panel__gpc-note">
              Applied automatically from your browser&rsquo;s Global Privacy Control signal.
            </p>
          )}
        </div>

        <div className="cookie-panel__actions">
          <button type="button" className="cookie-panel__save" onClick={() => save({ analytics, ads, optout })}>
            Save My Preferences
          </button>
          <button
            type="button"
            className="cookie-panel__eq"
            onClick={() => save({ analytics: false, ads: false, optout: true, reload: true })}
          >
            Reject All
          </button>
          <button
            type="button"
            className="cookie-panel__eq"
            onClick={() => save({ analytics: true, ads: true, optout: false })}
          >
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
};
